import { DOCUMENT_SUPPORTED_FORMAT_MAP } from "@finway-group/shared/lib/consts/common"
import { DocumentTypeEnum, type Expense, ExpenseStatusEnum, type Hospitality, type InboxInvoice, type InvoiceInterface, type OcrResult } from "@finway-group/shared/lib/models"
import { ExpenseDocumentTypeEnum } from "@finway-group/shared/lib/models/expense/expenseDocumentType.enum"
import { ExpensePaymentOptionEnum } from "@finway-group/shared/lib/models/expense/expensePaymentOption.enum"
import { type LineItem } from "@finway-group/shared/lib/models/expense/expenseSplit.model"
import { Card, Form, Modal, Spin, Switch, Tabs, Tooltip } from "antd"
import { type FormInstance } from "antd/lib/form"
import React, { Suspense, useEffect, useRef, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { DocumentTabManager } from "~Components/DocumentTabManager"
import { ExpenseRules } from "~Components/forms/rules"
import { type ExpenseData, useExpenseFormContext } from "~Components/modals/expenseCreateForm.context"
import { AIIcon } from "~Features/pages/purchaseOrders/icons/AIIcon"
import { fetchPurchaseOrdersForLinking } from "~Features/pages/purchaseOrders/queries/purchaseOrder.queries"
import { useModal } from "~Shared/context/modal.context"
import { SelectedInboxInvoicesProvider } from "~Shared/context/selectedExpenses.context"
import { useLoggedInEmployee } from "~Shared/hooks/employee.hooks"
import { fillFormWithOcrData } from "~Shared/hooks/ocr.hooks"
import { useTaxes } from "~Shared/hooks/tax.hooks"
import { ExpenseService, FileService, NotificationService, OcrService } from "~Shared/services"
import { NotificationTypeEnum } from "~Shared/services/notification.service"
import {
    getZeroTaxAmountsForSelfIssuedReceipt,
    isExpenseAlreadyPaid,
    isHospitalityExpense,
    mapDocumentTypeToUploadKind,
    restoreLinkedItemVatRatesFromPurchaseOrderItems,
} from "~Shared/utils/expense.utils"
import { convertFileDataFormat, flattenArray, getTooltipPopupContainer, isEmptyObject, isNotSet, mapInboxInvoiceToInvoice, mapToInvoiceInterface } from "~Shared/utils/helper.utils"
import { lazily } from "~Shared/utils/react.utils"
import { SelfIssuedReceiptConfirmationModal } from "../../../modals/SelfIssuedReceiptConfirmationModal"

const { InboxInvoiceModal } = lazily(() => import("~Components/modals/inboxInvoice/inboxInvoice.modal"))

interface CommonDocumentUploadProps {
    expenseForm: FormInstance
    isSubscription: boolean
    ocrSettings?: {
        isOcrEnabled: boolean
        isOcrItemizationEnabled: boolean
        isOcrItemizationDisplayed: boolean
        setIsOcrEnabled: (flag: boolean) => void
        setIsOcrItemizationEnabled: (flag: boolean) => void
    }
    expenseInvoices: Array<InvoiceInterface>
    expenseDeliveryNote?: string
    isUploading: boolean
    setIsUploading: (flag: boolean) => void
    expense: Expense | Hospitality
    updateExpense: (data: ExpenseData) => void
    inboxInvoices?: Array<InvoiceInterface>
    invoicesRequired?: boolean
    isNew?: boolean
    expenseOffers?: Array<string>
    setIsOcrProcessed: (flag: boolean) => void
    activeTab: string
    onTabClick(activeKey: string, _event: React.KeyboardEvent | React.MouseEvent): void
    ocrUpdateCallback?: (updatedOcrObject: OcrResult | null) => Promise<boolean>
}

export const CommonDocumentUpload = ({
    expenseForm,
    isSubscription,
    expenseInvoices,
    expenseDeliveryNote,
    ocrSettings,
    isUploading,
    setIsUploading,
    expense,
    updateExpense,
    inboxInvoices,
    invoicesRequired = false,
    isNew,
    expenseOffers,
    setIsOcrProcessed,
    activeTab,
    onTabClick,
    ocrUpdateCallback,
}: CommonDocumentUploadProps) => {
    const { t } = useTranslation()
    const [{ expense: formExpense, isFormTouched, setPrefilledVendorData, registerInboxInvoicesForDeletion, isDocumentTypeSetByUser }] = useExpenseFormContext()
    const currentFormStateRef = useRef({ expense: formExpense, isDocumentTypeSetByUser })
    currentFormStateRef.current = { expense: formExpense, isDocumentTypeSetByUser }
    const isSelfIssuedReceipt = Form.useWatch("isSelfIssuedReceipt", expenseForm) ?? false
    const [invoices, setInvoices] = useState<Array<InvoiceInterface>>(inboxInvoices ?? expenseInvoices)
    const [showInboxModal, setShowInboxModal] = useState(false)
    const [deliveryNote, setDeliveryNote] = useState(expenseDeliveryNote)
    const [isOcrUploading, setIsOcrUploading] = useState(false)
    const { invoiceUpload } = ExpenseRules({ invoices, skipInvoiceUpload: isSelfIssuedReceipt })
    const [activeInvoiceTabKey, setActiveInvoiceTabKey] = useState(0)
    const [invoicesOcrProcessed, setInvoicesOcrProcessed] = useState(inboxInvoices ?? expenseInvoices)
    const [offers, setOffers] = useState([...(expenseOffers || [])])
    const taxes = useTaxes()
    const loggedInEmployee = useLoggedInEmployee()
    const isAlreadyPaid = isExpenseAlreadyPaid(expense)
    const [showStampOnInvoice, setShowStampOnInvoice] = useState(true)
    const [updatedOcrObject, setUpdatedObject] = useState<OcrResult | null>(null)
    const { expenseEditable } = ExpenseService.calculateExpenseEditButtonView(expense, loggedInEmployee, t)
    const disableChanges = !expenseEditable
    const isHospitality = isHospitalityExpense(expense)
    const shouldEnableOcrItemization = isHospitality || !!ocrSettings?.isOcrItemizationEnabled
    const [isSelfIssuedReceiptModalOpen, setIsSelfIssuedReceiptModalOpen] = useState(false)

    const { showModal } = useModal()

    useEffect(() => {
        if (isSelfIssuedReceipt) {
            ocrSettings?.setIsOcrEnabled(false)
            ocrSettings?.setIsOcrItemizationEnabled(false)
        }
    }, [isSelfIssuedReceipt])

    const getVatRatesRestoredFromLinkedPurchaseOrders = async () => {
        const purchaseOrderIds = [
            ...new Set(
                (expense.splits ?? []).flatMap((split) => {
                    const purchaseOrderId = (split as LineItem).purchaseOrderLink?._id
                    return purchaseOrderId ? [String(purchaseOrderId)] : []
                }),
            ),
        ]
        if (purchaseOrderIds.length === 0) {
            return {}
        }

        try {
            const purchaseOrders = await fetchPurchaseOrdersForLinking({
                filter: { _id: { in: purchaseOrderIds } },
                limit: purchaseOrderIds.length,
                page: 1,
            })
            return (
                restoreLinkedItemVatRatesFromPurchaseOrderItems({
                    expense,
                    purchaseOrderLineItems: purchaseOrders.docs.flatMap((purchaseOrder) => purchaseOrder.lineItems ?? []),
                    taxes,
                }) ?? {}
            )
        } catch {
            NotificationService.send(
                NotificationTypeEnum.ERROR,
                t("notification:self_issued_receipt.vat_restore_failed.title"),
                t("notification:self_issued_receipt.vat_restore_failed.message"),
            )
            return {}
        }
    }

    const getTaxUpdateForSelfIssuedReceiptToggle = async (enabled: boolean) => {
        if (enabled) {
            return getZeroTaxAmountsForSelfIssuedReceipt(expense)
        }
        // Cancelling the enable dialog also calls this with false, before the flag was ever set. Skip the purchase-order VAT restore so existing rates stay put.
        if (!isSelfIssuedReceipt) {
            return {}
        }
        return getVatRatesRestoredFromLinkedPurchaseOrders()
    }

    const setSelfIssuedReceiptEnabled = async (enabled: boolean) => {
        const clearedReason = enabled ? {} : { selfIssuedReceiptReason: undefined }
        const taxUpdate = await getTaxUpdateForSelfIssuedReceiptToggle(enabled)
        expenseForm.setFieldsValue({ isSelfIssuedReceipt: enabled, ...clearedReason, ...taxUpdate })
        updateExpense({ isSelfIssuedReceipt: enabled, ...clearedReason, ...taxUpdate })
    }

    const fillForm = (ocrObject: OcrResult) => {
        const currentInvoice = invoices[activeInvoiceTabKey]
        fillFormWithOcrData({
            formInstance: expenseForm,
            expense,
            ocrObject,
            taxes,
            updateExpense,
            setPrefilledVendorData,
            t,
            ocrItemizationEnabled: shouldEnableOcrItemization,
            getCurrentFormState: () => currentFormStateRef.current,
        }).finally(() => {
            setInvoicesOcrProcessed([...invoicesOcrProcessed, currentInvoice])
            setIsOcrProcessed(true)
            NotificationService.send(NotificationTypeEnum.INFO, t("notification:ocr.processed.title"), t("notification:ocr.processed.message"))
        })
    }

    /** @returns `false` if the user cancelled adding the expense after being warned about number mismatches, `true` otherwise. */
    const noDataDifferenceFunction = async (ocrObject: OcrResult) => {
        const isCancelledByUser = ocrUpdateCallback && !(await ocrUpdateCallback(ocrObject))
        if (!isCancelledByUser) {
            fillForm(ocrObject)
        }
        return !isCancelledByUser
    }
    // to update the form with the ocr data
    useEffect(() => {
        if (updatedOcrObject) {
            fillForm(updatedOcrObject)
        }
    }, [updatedOcrObject])

    /** @returns `false` if the user cancelled adding the document after being warned about number mismatches, `true` otherwise. */
    const handleOcr = async ({
        ocrInvoices,
        isOcrEnabled,
        isOcrRetrigger,
        method,
        onOcrComplete,
        ocrInvoice,
        ignoreDuplicateInvoiceIds,
    }: {
        ocrInvoices: Array<InvoiceInterface>
        isOcrEnabled: boolean
        isOcrRetrigger: boolean
        method: string
        onOcrComplete: (_: { addOcrResultsToInvoices: (invoice: InvoiceInterface) => InvoiceInterface }) => Promise<void>
        ocrInvoice?: InvoiceInterface
        ignoreDuplicateInvoiceIds?: Array<string>
    }): Promise<boolean> => {
        if (!isOcrEnabled) {
            return true
        }

        const currentInvoice = ocrInvoice ?? ocrInvoices[activeInvoiceTabKey]
        if (!currentInvoice) {
            return true
        }

        return OcrService.runOcr({
            invoiceUrl: currentInvoice?.url,
            expense,
            setIsOcrUploading,
            t,
            noDataDifferenceFunction,
            setUpdatedObject,
            showModal,
            language: loggedInEmployee?.getUserLanguage(),
            isOcrItemizationEnabled: shouldEnableOcrItemization,
            isShowingVendorChangeIfNotFound: true,
            showOcrDifferencesModal: !isNew || isFormTouched,
            isOcrRetrigger,
            ignoreDuplicateInvoiceIds,
            meta: { source: `commonDocumentUpload.form.tsx#${method}` },
            isDocumentTypeSetByUser,
            handleInvoice: async (ocrInvoiceData) => {
                const addOcrResultsToInvoices = (invoice: InvoiceInterface) => {
                    if (invoice.fileId === currentInvoice.fileId) {
                        return { ...invoice, isEInvoice: ocrInvoiceData?.isEInvoice, invoiceData: ocrInvoiceData }
                    }
                    return invoice
                }
                onOcrComplete({ addOcrResultsToInvoices })
            },
        })
    }

    const doFileUpload = async (files: Array<File>, kind: DocumentTypeEnum) => {
        if (!FileService.checkFilesSizeAndHandleError(files)) {
            return
        }
        if (!FileService.checkAllowedInvoicesAmountAndHandleError(files, expense.invoices?.length ?? 0)) {
            return
        }
        const isFileExtensionInvalid = files.some((file) => !FileService.checkIsFileExtensionValidAndShowNotification(file, DOCUMENT_SUPPORTED_FORMAT_MAP[kind]))
        if (isFileExtensionInvalid) {
            return
        }

        setIsUploading(true)
        try {
            const data = await FileService.uploadMultiple(files, mapDocumentTypeToUploadKind(kind))
            switch (kind) {
                case DocumentTypeEnum.INVOICE:
                    const docsToAdd: Array<InvoiceInterface> = []
                    for (const fileUploadData of data) {
                        if (await ExpenseService.checkForDuplicateInvoiceFile(fileUploadData.duplicateData)) {
                            docsToAdd.push(convertFileDataFormat(fileUploadData))
                        }
                    }
                    const formInvoices = expenseForm.getFieldValue("invoices")
                    let allInvoices = flattenArray<InvoiceInterface>([...formInvoices, ...docsToAdd])

                    const isCancelledByUser = !(await handleOcr({
                        ocrInvoices: allInvoices,
                        isOcrEnabled: ocrSettings?.isOcrEnabled ?? false,
                        isOcrRetrigger: false,
                        method: "doFileUpload",
                        onOcrComplete: async ({ addOcrResultsToInvoices }) => {
                            allInvoices = allInvoices.map(addOcrResultsToInvoices)
                        },
                    }))

                    if (isCancelledByUser) {
                        allInvoices = allInvoices.filter((_, i) => i !== activeInvoiceTabKey)
                    }

                    setInvoices(allInvoices)
                    updateExpense({ invoices: allInvoices })
                    break
                case DocumentTypeEnum.DELIVERY_NOTE:
                    const newFileData = convertFileDataFormat(data[0])
                    setDeliveryNote(newFileData?.url)
                    updateExpense({ deliveryNote: newFileData?.url })
                    break
                case DocumentTypeEnum.OFFER:
                    const formOffers = expenseForm.getFieldValue("offers")
                    const newOfferUrls = data.map((fileData) => convertFileDataFormat(fileData)?.url)
                    setOffers(flattenArray([...formOffers, ...newOfferUrls]))
                    updateExpense({ offers: flattenArray([...formOffers, ...newOfferUrls]) })
                    break
                default:
                    break
            }
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:file.upload.title"))
        } finally {
            setIsUploading(false)
        }
    }

    const onInboxSelected = (event?: React.MouseEvent) => {
        event?.stopPropagation()
        setShowInboxModal(true)
    }

    const applyInboxOcr = async (allInvoices: Array<InvoiceInterface>, ocrSource: InboxInvoice, ignoreDuplicateInvoiceIds: Array<string>) => {
        const isOcrEnabled = ocrSettings?.isOcrEnabled ?? false
        if (!isOcrEnabled) {
            return
        }

        const { ocrResults } = ocrSource
        if (isNotSet(ocrResults) || isEmptyObject(ocrResults)) {
            await handleOcr({
                ocrInvoices: allInvoices,
                ocrInvoice: allInvoices.find((invoice) => invoice.fileId === ocrSource.fileId),
                isOcrEnabled,
                isOcrRetrigger: false,
                ignoreDuplicateInvoiceIds,
                method: "handleInboxAttachToForm",
                onOcrComplete: async ({ addOcrResultsToInvoices }) => {
                    const invoicesWithOcrResults = allInvoices.map(addOcrResultsToInvoices)
                    setInvoices(invoicesWithOcrResults)
                    updateExpense({ invoices: invoicesWithOcrResults })
                },
            })
            return
        }

        setIsOcrUploading(true)
        try {
            await OcrService.applyOcrResult({
                ocrObject: ocrResults,
                expense,
                t,
                noDataDifferenceFunction,
                setUpdatedObject,
                showModal,
                language: loggedInEmployee?.getUserLanguage(),
                isShowingVendorChangeIfNotFound: true,
                showOcrDifferencesModal: !isNew || isFormTouched,
                ignoreDuplicateInvoiceIds,
                isDocumentTypeSetByUser,
                // mapInboxInvoiceToInvoice already carried the e-invoice data over.
                handleInvoice: async () => {},
            })
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:ocr.title"))
        } finally {
            setIsOcrUploading(false)
        }
    }

    const handleInboxAttachToForm = async (selectedInboxInvoices: Array<InboxInvoice>, ocrSource?: InboxInvoice) => {
        if (!selectedInboxInvoices.length) {
            return
        }
        const formInvoices: Array<InvoiceInterface> = expenseForm.getFieldValue("invoices") ?? []
        const existingFileIds = new Set(formInvoices.map((invoice) => invoice.fileId))
        const inboxInvoicesToAttach = selectedInboxInvoices.filter((invoice) => !existingFileIds.has(invoice.fileId))
        if (!inboxInvoicesToAttach.length) {
            return
        }
        if (!FileService.checkAllowedInvoicesAmountAndHandleError(inboxInvoicesToAttach, formInvoices.length)) {
            return
        }
        const allInvoices = flattenArray<InvoiceInterface>([...formInvoices, ...inboxInvoicesToAttach.map(mapInboxInvoiceToInvoice)])
        setInvoices(allInvoices)
        updateExpense({ invoices: allInvoices })
        registerInboxInvoicesForDeletion(inboxInvoicesToAttach)

        if (ocrSource) {
            const ignoreDuplicateInvoiceIds = inboxInvoicesToAttach.map((invoice) => invoice.id || invoice._id)
            await applyInboxOcr(allInvoices, ocrSource, ignoreDuplicateInvoiceIds)
        }
    }

    const onRemoveTab = (documentType: DocumentTypeEnum, targetKey: string | React.MouseEvent | React.KeyboardEvent) => {
        switch (documentType) {
            case DocumentTypeEnum.INVOICE:
                let invoicesCopy = [...invoices]
                invoicesCopy = invoicesCopy.filter((_, i) => i !== Number(targetKey))
                setInvoices(invoicesCopy)
                const formInvoices = expenseForm.getFieldValue("invoices") as Array<InvoiceInterface>
                updateExpense({ invoices: formInvoices.filter((_, i) => i !== Number(targetKey)) })
                break
            case DocumentTypeEnum.DELIVERY_NOTE:
                setDeliveryNote(undefined)
                updateExpense({ deliveryNote: undefined })
                break
            case DocumentTypeEnum.OFFER:
                let offerCopy = [...offers]
                offerCopy = offerCopy.filter((_, i) => i !== Number(targetKey))
                setOffers(offerCopy)
                updateExpense({ offers: offerCopy })
                break
            default:
                break
        }
    }

    const onChangeInvoicesActiveKey = (newInvoiceTabKey: number) => {
        setActiveInvoiceTabKey(Math.max(newInvoiceTabKey, 0))
    }

    const toggles = (
        <div className="mr-10 ml-10 text-sm flex flex-row gap-10 items-center justify-end">
            {isOcrUploading && <Spin />}

            <Tooltip placement="bottom" title={t("tooltips:ai_ocr_hint")} color="#9747FF">
                <>
                    <AIIcon />
                </>
            </Tooltip>
            <Tooltip getPopupContainer={getTooltipPopupContainer} placement="topLeft" title={t("tooltips:ocr_hint")}>
                <span>{t("input:settings.enable_ocr")}</span>
            </Tooltip>
            <Switch
                data-testid="enableOcrToggle"
                size="small"
                checked={ocrSettings?.isOcrEnabled}
                onChange={(checked) => {
                    void handleOcr({
                        ocrInvoices: invoices,
                        isOcrEnabled: checked,
                        isOcrRetrigger: true,
                        method: "toggles",
                        onOcrComplete: async ({ addOcrResultsToInvoices }) => {
                            setInvoices((oldInvoices) => oldInvoices.map(addOcrResultsToInvoices))
                            updateExpense({ invoices: invoices.map(addOcrResultsToInvoices) })
                        },
                    })
                    ocrSettings?.setIsOcrEnabled(checked)
                }}
                disabled={isOcrUploading || isSelfIssuedReceipt}
            />
            {ocrSettings?.isOcrItemizationDisplayed && (
                <>
                    <span>{t("input:settings.inline_items")}</span>
                    <Switch
                        data-testid="enableInlineItemsToggle"
                        disabled={!ocrSettings?.isOcrEnabled || isHospitalityExpense(expense) || isOcrUploading || isSelfIssuedReceipt}
                        size="small"
                        checked={ocrSettings?.isOcrItemizationEnabled}
                        onChange={(checked) => ocrSettings?.setIsOcrItemizationEnabled(checked)}
                    />
                </>
            )}

            {isAlreadyPaid && (
                <>
                    <span className="mr-10 ml-10">{t("input:settings.show_stamp")}</span>
                    <Switch size="small" defaultChecked={showStampOnInvoice} onChange={(checked) => setShowStampOnInvoice(checked)} />
                </>
            )}
        </div>
    )

    return (
        <>
            <Card className="w-full card-with-tabs h-full">
                <Tabs
                    activeKey={activeTab}
                    onTabClick={onTabClick}
                    className="w-full min-h-200 ml-1"
                    tabBarExtraContent={ocrSettings && activeTab === DocumentTypeEnum.INVOICE && toggles}
                    defaultActiveKey={DocumentTypeEnum.INVOICE}
                >
                    <Tabs.TabPane key={DocumentTypeEnum.OFFER} tab={t("input:request.offer")}>
                        <Form.Item name="offers" key="offers">
                            <DocumentTabManager
                                invoices={mapToInvoiceInterface(offers)}
                                doFileUpload={doFileUpload}
                                isUploading={isUploading}
                                onRemoveTab={onRemoveTab}
                                documentType={DocumentTypeEnum.OFFER}
                                label={{
                                    buttonLabel: t("action:add_offer"),
                                    draggerDescription: t("action:or_drop_files"),
                                }}
                                readOnly={disableChanges}
                            />
                        </Form.Item>
                    </Tabs.TabPane>
                    <Tabs.TabPane
                        key={DocumentTypeEnum.INVOICE}
                        tab={<span className={invoicesRequired && !isSelfIssuedReceipt ? `label-required` : ""}>{t("input:request.invoice")}</span>}
                    >
                        <Form.Item data-testid="invoiceRequestForm" name="invoices" key="invoices" rules={invoicesRequired && isNew ? invoiceUpload : []}>
                            <DocumentTabManager
                                invoices={invoices}
                                deliveryNote={deliveryNote}
                                doFileUpload={doFileUpload}
                                isUploading={isUploading}
                                onRemoveTab={onRemoveTab}
                                documentType={DocumentTypeEnum.INVOICE}
                                label={{
                                    buttonLabel: t("action:add_invoice"),
                                    draggerDescription: t("action:or_drop_files"),
                                }}
                                onInboxSelected={onInboxSelected}
                                onChangeInvoicesActiveKey={onChangeInvoicesActiveKey}
                                readOnly={
                                    expense.deleted ||
                                    (expense.status === ExpenseStatusEnum.DECLINED && expense.paymentOption === ExpensePaymentOptionEnum.SMART_CARD) ||
                                    disableChanges
                                }
                                showPaidStamp={isAlreadyPaid && showStampOnInvoice}
                                isGeneratedReceiptRemovable={!isSelfIssuedReceipt}
                            />
                        </Form.Item>
                        {formExpense.documentType !== ExpenseDocumentTypeEnum.CREDIT_NOTE && (
                            <div
                                className={[isSelfIssuedReceipt && "border-finway-blue-accent-dark", "border-1", isSelfIssuedReceipt && "bg-finway-blue-light", "rounded-lg", "p-2"]
                                    .filter(Boolean)
                                    .join(" ")}
                            >
                                <Form.Item className="m-8" name="isSelfIssuedReceipt" valuePropName="checked">
                                    <div className="flex flex-row items-center gap-16">
                                        <Switch
                                            className="mb-8"
                                            checked={isSelfIssuedReceipt}
                                            onChange={(checked) => {
                                                if (checked) {
                                                    setIsSelfIssuedReceiptModalOpen(true)
                                                } else {
                                                    Modal.confirm({
                                                        title: t("confirm:self_issued_receipt.discard.title"),
                                                        content: t("confirm:self_issued_receipt.discard.message"),
                                                        okText: t("confirm:self_issued_receipt.discard.confirm"),
                                                        cancelText: t("confirm:self_issued_receipt.discard.cancel"),
                                                        onOk: () => {
                                                            setSelfIssuedReceiptEnabled(false)
                                                        },
                                                    })
                                                }
                                            }}
                                        />
                                        <div>
                                            <span className="text-finway-blue-dark">{t("input:request.self_issued_receipt.label")}</span>
                                            <p>
                                                <Trans
                                                    t={t}
                                                    i18nKey="info:self_issued_receipt"
                                                    components={[
                                                        <a key="help-center" href={t("info:self_issued_receipt_link")} target="_blank" rel="noreferrer" className="underline" />,
                                                    ]}
                                                />
                                            </p>
                                        </div>
                                    </div>
                                </Form.Item>
                            </div>
                        )}
                    </Tabs.TabPane>

                    {!isSubscription && deliveryNote && (
                        <Tabs.TabPane key={DocumentTypeEnum.DELIVERY_NOTE} tab={t("input:request.delivery_note")}>
                            <Form.Item name="deliveryNote" key="deliveryNote">
                                <DocumentTabManager
                                    invoices={invoices}
                                    deliveryNote={deliveryNote}
                                    doFileUpload={doFileUpload}
                                    onRemoveTab={onRemoveTab}
                                    documentType={DocumentTypeEnum.DELIVERY_NOTE}
                                    label={{
                                        buttonLabel: t("action:add_delivery_note"),
                                        draggerDescription: t("action:or_drop_files"),
                                    }}
                                    readOnly={true}
                                />
                            </Form.Item>
                        </Tabs.TabPane>
                    )}
                </Tabs>
            </Card>
            {showInboxModal && (
                <SelectedInboxInvoicesProvider>
                    <Suspense fallback={null}>
                        <InboxInvoiceModal
                            isShowing={showInboxModal}
                            onCancel={() => setShowInboxModal(false)}
                            expenses={[expense as Expense]}
                            isOcrEnabled={ocrSettings?.isOcrEnabled ?? false}
                            isOcrItemizationEnabled={ocrSettings?.isOcrItemizationEnabled ?? false}
                            shouldResetSelectedInboxOnClose
                            onAttachToForm={handleInboxAttachToForm}
                            excludedFileIds={invoices.map((invoice) => invoice.fileId).filter(Boolean)}
                        />
                    </Suspense>
                </SelectedInboxInvoicesProvider>
            )}
            <SelfIssuedReceiptConfirmationModal
                open={isSelfIssuedReceiptModalOpen}
                onConfirm={() => {
                    void setSelfIssuedReceiptEnabled(true)
                    setIsSelfIssuedReceiptModalOpen(false)
                }}
                onCancel={() => {
                    void setSelfIssuedReceiptEnabled(false)
                    setIsSelfIssuedReceiptModalOpen(false)
                }}
            />
        </>
    )
}
